import { ApisauceInstance } from 'apisauce';

import getDriverRewardsOutput from '../api/getDriverRewardsOutput';
import fetchWeeklyRewardRecords from './fetchWeeklyRewardRecords';

/**
 * Resolve a driver's reward output for the period being viewed.
 *
 * Ordinary (single-window) programmes use `GET /rewards`, keyed on the parent
 * period. Sub-period programmes need an individual week's record, but GET /rewards
 * only addresses the parent period and returns a single record — so we read the
 * per-week records straight from Firestore (one doc per sub-period, keyed on
 * driver + parent period) and pick the one whose window matches the selected
 * sub-period, falling back to the driver's most recent available week when the
 * selected week has no record (drivers skip weeks with no activity, and the
 * Approvals picker only navigates whole periods). Every override endpoint keys on
 * the returned record's `bonusDatasetRecordId`, so no override API needs a
 * sub-period concept.
 *
 * NOTE: the Firestore `driverBonusRewards` collection is deny-by-default in
 * firestore.rules and only opened for the account(s) using the sub-period pattern
 * (currently WEL only). This branch is reached only when `subPeriod` is set, which
 * only happens for those programmes — so other accounts never hit the read.
 */
const resolveDriverRewardOutput = async ({
  api,
  accountId,
  driver,
  bonusProgramId,
  bonusPeriodId,
  subPeriod,
}: {
  api: ApisauceInstance;
  accountId: string;
  driver: string;
  bonusProgramId: string;
  bonusPeriodId: string;
  subPeriod: Period | undefined;
}): Promise<BonusTypes.Rewards.AnyDriverRewardsOutput | undefined> => {
  if (subPeriod) {
    const records = await fetchWeeklyRewardRecords({
      accountId,
      driver,
      bonusProgramId,
      bonusPeriodId,
    });

    // Prefer the exact selected week.
    const exact = records.find(
      (record) =>
        record.startDate === subPeriod.startDate &&
        record.endDate === subPeriod.endDate,
    );
    if (exact) {
      return exact;
    }

    // The driver has records for this period but not the selected week — e.g. a
    // week skipped for zero activity, or (on Approvals, where the picker lists
    // whole periods) a week the user can't navigate to. Fall back to their most
    // recent available week rather than treating them as absent from the period.
    // Only a driver with no records at all resolves to undefined (→ deselect +
    // "not available"), which correctly means "not in this period".
    return records
      .slice()
      .sort((a, b) => b.endDate.localeCompare(a.endDate))[0];
  }

  const response = await getDriverRewardsOutput({
    api,
    driver,
    bonusProgramId,
    intervalId: bonusPeriodId,
  });
  return response.ok && response.data ? response.data : undefined;
};

export default resolveDriverRewardOutput;
